import React, { useState } from 'react';
import { DollarSign, Percent, Calendar, TrendingUp, AlertCircle, Save } from 'lucide-react';

function TabNegocio({ configuracion, onSave, showToast, setHasChanges }) {
  const [formData, setFormData] = useState({
    iva_porcentaje: configuracion.iva_porcentaje || 16,
    tarifa_naviera_default: configuracion.tarifa_naviera_default || 140,
    margen_venta: configuracion.margen_venta || 15,
    dias_libres_default: configuracion.dias_libres_default || 21,
    rango_dias_min: configuracion.rango_dias_min || 1,
    rango_dias_max: configuracion.rango_dias_max || 999
  });

  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (saving) return;

    // Validaciones
    if (formData.iva_porcentaje < 0 || formData.iva_porcentaje > 100) {
      showToast('El IVA debe estar entre 0% y 100%', 'error');
      return;
    }

    if (formData.tarifa_naviera_default <= 0) {
      showToast('La tarifa naviera debe ser mayor a 0', 'error');
      return;
    }

    if (formData.margen_venta < 0) {
      showToast('El margen de venta no puede ser negativo', 'error');
      return;
    }

    if (formData.dias_libres_default <= 0) {
      showToast('Los días libres deben ser mayor a 0', 'error');
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  // Calcular ejemplo en tiempo real
  const ivaMultiplicador = 1 + (formData.iva_porcentaje / 100);
  const diasEjemplo = 10;
  const costoSinIVA = diasEjemplo * formData.tarifa_naviera_default;
  const costoConIVA = costoSinIVA * ivaMultiplicador;
  const ventaSinIVA = diasEjemplo * (formData.tarifa_naviera_default + formData.margen_venta);
  const ventaConIVA = ventaSinIVA * ivaMultiplicador;
  const utilidad = ventaSinIVA - costoSinIVA;

  return (
    <div className="space-y-6">
      {/* Alerta informativa */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">Parámetros Críticos de Negocio</p>
          <p>Estos valores afectan los cálculos de costos, ventas y utilidades en todo el sistema. Los cambios se aplicarán inmediatamente.</p>
        </div>
      </div>

      {/* Impuestos */}
      <div className="border border-ink-200 rounded-lg p-6 bg-white">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Percent size={20} className="text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-ink-900">Impuestos</h3>
            <p className="text-sm text-ink-500">Porcentaje de IVA aplicado</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            IVA (%)
          </label>
          <div className="flex items-center gap-3">
            <input
              type="number"
              value={formData.iva_porcentaje}
              onChange={(e) => handleChange('iva_porcentaje', e.target.value)}
              min="0"
              max="100"
              step="0.01"
              className="flex-1 px-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-900 focus:border-transparent"
            />
            <span className="text-ink-500 font-medium">
              = {ivaMultiplicador.toFixed(4)}x multiplicador
            </span>
          </div>
          <p className="text-xs text-ink-400 mt-1">
            Actualmente: {formData.iva_porcentaje}% → Todos los cálculos usan {ivaMultiplicador.toFixed(2)}x
          </p>
        </div>
      </div>

      {/* Tarifas por Defecto */}
      <div className="border border-ink-200 rounded-lg p-6 bg-white">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <DollarSign size={20} className="text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold text-ink-900">Tarifas por Defecto</h3>
            <p className="text-sm text-ink-500">Valores cuando no hay tarifanav configurada</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tarifa Naviera Base (USD/día)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400">$</span>
              <input
                type="number"
                value={formData.tarifa_naviera_default}
                onChange={(e) => handleChange('tarifa_naviera_default', e.target.value)}
                min="0"
                step="0.01"
                className="w-full pl-8 pr-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-900 focus:border-transparent"
              />
            </div>
            <p className="text-xs text-ink-400 mt-1">
              Usado cuando naviera no tiene tarifa configurada
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Margen de Venta (USD/día)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400">$</span>
              <input
                type="number"
                value={formData.margen_venta}
                onChange={(e) => handleChange('margen_venta', e.target.value)}
                min="0"
                step="0.01"
                className="w-full pl-8 pr-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-900 focus:border-transparent"
              />
            </div>
            <p className="text-xs text-ink-400 mt-1">
              Utilidad añadida por día de demora
            </p>
          </div>
        </div>
      </div>

      {/* Políticas de Demoras */}
      <div className="border border-ink-200 rounded-lg p-6 bg-white">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Calendar size={20} className="text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-ink-900">Políticas de Demoras</h3>
            <p className="text-sm text-ink-500">Días y rangos permitidos</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Días Libres por Defecto
            </label>
            <input
              type="number"
              value={formData.dias_libres_default}
              onChange={(e) => handleChange('dias_libres_default', e.target.value)}
              min="1"
              step="1"
              className="w-full px-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-900 focus:border-transparent"
            />
            <p className="text-xs text-ink-400 mt-1">
              Cuando naviera no tiene política definida
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rango Días - Desde
            </label>
            <input
              type="number"
              value={formData.rango_dias_min}
              onChange={(e) => handleChange('rango_dias_min', e.target.value)}
              min="1"
              step="1"
              className="w-full px-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-900 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rango Días - Hasta
            </label>
            <input
              type="number"
              value={formData.rango_dias_max}
              onChange={(e) => handleChange('rango_dias_max', e.target.value)}
              min="1"
              step="1"
              className="w-full px-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-900 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Ejemplo de Cálculo */}
      <div className="border-2 border-green-200 bg-green-50 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <TrendingUp size={24} className="text-green-700" />
          <div>
            <h3 className="font-semibold text-green-900">Ejemplo de Cálculo (10 días de demora)</h3>
            <p className="text-sm text-green-700">Vista previa con los valores actuales</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Costo sin IVA</p>
            <p className="text-lg font-bold text-gray-900">${costoSinIVA.toFixed(2)}</p>
            <p className="text-xs text-gray-400">{diasEjemplo} × ${formData.tarifa_naviera_default}</p>
          </div>

          <div className="bg-white rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Costo con IVA</p>
            <p className="text-lg font-bold text-red-600">${costoConIVA.toFixed(2)}</p>
            <p className="text-xs text-gray-400">× {ivaMultiplicador.toFixed(2)}</p>
          </div>

          <div className="bg-white rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Venta con IVA</p>
            <p className="text-lg font-bold text-green-600">${ventaConIVA.toFixed(2)}</p>
            <p className="text-xs text-gray-400">{diasEjemplo} × (${formData.tarifa_naviera_default} + ${formData.margen_venta})</p>
          </div>

          <div className="bg-white rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Utilidad</p>
            <p className="text-lg font-bold text-blue-600">${utilidad.toFixed(2)}</p>
            <p className="text-xs text-gray-400">{((utilidad / ventaSinIVA) * 100).toFixed(1)}% margen</p>
          </div>
        </div>

        <div className="mt-4 p-3 bg-white rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>Fórmula:</strong> Utilidad = (Días × Margen) = {diasEjemplo} × ${formData.margen_venta} = ${utilidad.toFixed(2)}
          </p>
        </div>
      </div>

      {/* Botón Guardar */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-ink-900 text-white rounded-lg hover:bg-ink-800 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Guardando...</span>
            </>
          ) : (
            <>
              <Save size={18} />
              <span>Guardar Configuración</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default TabNegocio;
